// Image XObjects and inline images (ISO 32000-1 §8.9): raw sample data, DCT (through the browser's JPEG decoder), masks and soft masks.

import { decodeStream } from "./filters";
import { DEVICE_CMYK, DEVICE_GRAY, DEVICE_RGB, makeColorSpace, type ColorSpace } from "./color";
import { Dict, PName, Stream, type PObj, type XRef } from "./parse";

export interface PdfImage {
    /** RGBA pixels, or null for a stencil mask. */
    canvas: HTMLCanvasElement | OffscreenCanvas;
    width: number;
    height: number;
    /** A stencil: the canvas holds black where the current fill colour paints. */
    stencil: boolean;
}

function makeCanvas(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
    try { return new OffscreenCanvas(w, h); } catch { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; }
}

const nums = (o: PObj, xref: XRef): number[] => (Array.isArray(o) ? o.map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; }) : []);

/** The samples of a stream as 8-bit-per-component values with the component count of the colour space. */
function unpack(data: Uint8Array, w: number, h: number, n: number, bpc: number): Uint16Array {
    const out = new Uint16Array(w * h * n);
    const rowBytes = (w * n * bpc + 7) >> 3;
    if (bpc === 8) { for (let y = 0; y < h; y++) { const s = y * rowBytes, d = y * w * n; for (let i = 0; i < w * n; i++) out[d + i] = data[s + i] ?? 0; } return out; }
    if (bpc === 16) { for (let y = 0; y < h; y++) { const s = y * rowBytes, d = y * w * n; for (let i = 0; i < w * n; i++) out[d + i] = (((data[s + 2 * i] ?? 0) << 8) | (data[s + 2 * i + 1] ?? 0)) >> 8; } return out; }
    const mask = (1 << bpc) - 1;
    for (let y = 0; y < h; y++) {
        let bit = y * rowBytes * 8;
        for (let i = 0; i < w * n; i++) {
            out[y * w * n + i] = ((data[bit >> 3] ?? 0) >> (8 - bpc - (bit & 7))) & mask;
            bit += bpc;
        }
    }
    return out;
}

async function decodeJpeg(bytes: Uint8Array): Promise<ImageBitmap | null> {
    try { return await createImageBitmap(new Blob([bytes as BlobPart], { type: "image/jpeg" })); } catch { return null; }
}

/** Decodes an image to a canvas. `res` resolves named colour spaces. */
export async function decodeImage(s: Stream, res: Dict | null, xref: XRef, inlineAbbrev = false): Promise<PdfImage | null> {
    const d = s.dict;
    const get = (k: string, ab?: string) => d.get(k, ab);
    const w = Math.round((get("Width", inlineAbbrev ? "W" : undefined) as number) || 0), h = Math.round((get("Height", inlineAbbrev ? "H" : undefined) as number) || 0);
    if (w <= 0 || h <= 0 || w * h > 120_000_000) return null;
    const isMask = get("ImageMask", inlineAbbrev ? "IM" : undefined) === true;
    let bpc = (get("BitsPerComponent", inlineAbbrev ? "BPC" : undefined) as number) || (isMask ? 1 : 8);
    const dec = decodeStream(s, true);
    const decodeArr = nums(get("Decode", inlineAbbrev ? "D" : undefined), xref);
    const csObj = get("ColorSpace", inlineAbbrev ? "CS" : undefined);
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    const img = ctx.createImageData(w, h);
    const px = img.data;

    if (dec.image === "DCTDecode") {
        const bm = await decodeJpeg(dec.data);
        if (!bm) return placeholder(w, h);
        ctx.drawImage(bm, 0, 0, w, h);
        let out: PdfImage = { canvas, width: w, height: h, stencil: false };
        // an Indexed or inverted decode is rare for JPEG; honour CMYK-style Decode [1 0 …] only for gray
        out = await applyMasks(out, d, res, xref);
        return out;
    }
    if (dec.image === "JPXDecode" || dec.image === "JBIG2Decode") return placeholder(w, h);
    if (dec.image === "CCITTFaxDecode") {
        const g = ccitt(dec.data, w, h, dec.parms);
        if (!g) return placeholder(w, h);
        // 1 bit per pixel, 0 = black unless BlackIs1
        const bi = g.blackIs1;
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const bitv = (g.data[y * g.rowBytes + (x >> 3)]! >> (7 - (x & 7))) & 1;
            const black = bi ? bitv === 1 : bitv === 0;
            const i = (y * w + x) * 4;
            if (isMask) { px[i + 3] = black !== (decodeArr[0] === 1) ? 255 : 0; }
            else { const v = black ? 0 : 255; px[i] = px[i + 1] = px[i + 2] = v; px[i + 3] = 255; }
        }
        ctx.putImageData(img, 0, 0);
        return applyMasks({ canvas, width: w, height: h, stencil: isMask }, d, res, xref);
    }

    if (isMask) {
        const data = dec.data;
        const samples = unpack(data, w, h, 1, 1);
        const invert = decodeArr[0] === 1;
        for (let i = 0; i < w * h; i++) {
            const paint = (samples[i] === 0) !== invert;
            px[i * 4 + 3] = paint ? 255 : 0;
        }
        ctx.putImageData(img, 0, 0);
        return { canvas, width: w, height: h, stencil: true };
    }
    let cs: ColorSpace = csObj ? makeColorSpace(csObj, res, xref) : DEVICE_GRAY;
    if (cs.name === "Pattern") cs = DEVICE_GRAY;
    const n = cs.n || 1;
    if (![1, 2, 4, 8, 16].includes(bpc)) bpc = 8;
    const samples = unpack(dec.data, w, h, n, bpc);
    const maxv = bpc === 16 ? 255 : (1 << bpc) - 1;
    const dflt = cs.decode(bpc);
    const dc = decodeArr.length >= 2 * n ? decodeArr : dflt;
    const isDefault = dc.every((v, i) => v === dflt[i]);
    const colorKey = nums(get("Mask"), xref);
    if (cs === DEVICE_RGB && bpc === 8 && isDefault && !colorKey.length) {
        for (let i = 0, j = 0; i < w * h; i++, j += 3) { px[i * 4] = samples[j]!; px[i * 4 + 1] = samples[j + 1]!; px[i * 4 + 2] = samples[j + 2]!; px[i * 4 + 3] = 255; }
    } else if (cs === DEVICE_GRAY && isDefault && !colorKey.length) {
        const scale = 255 / maxv;
        for (let i = 0; i < w * h; i++) { const v = Math.round(samples[i]! * scale); px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = v; px[i * 4 + 3] = 255; }
    } else {
        const comps = new Array<number>(n);
        const cache = new Map<number, [number, number, number]>();
        for (let i = 0; i < w * h; i++) {
            let key = 0, keyed = n <= 4;
            for (let c = 0; c < n; c++) { const raw = samples[i * n + c]!; if (keyed) key = key * 256 + raw; const lo = dc[2 * c] ?? 0, hi = dc[2 * c + 1] ?? 1; comps[c] = lo + (raw / maxv) * (hi - lo); }
            let rgb = keyed ? cache.get(key) : undefined;
            if (!rgb) { rgb = cs.rgb(comps); if (keyed && cache.size < 70000) cache.set(key, rgb); }
            px[i * 4] = rgb[0]; px[i * 4 + 1] = rgb[1]; px[i * 4 + 2] = rgb[2]; px[i * 4 + 3] = 255;
            if (colorKey.length >= 2 * n) {
                let masked = true;
                for (let c = 0; c < n; c++) { const raw = samples[i * n + c]!; if (raw < colorKey[2 * c]! || raw > colorKey[2 * c + 1]!) { masked = false; break; } }
                if (masked) px[i * 4 + 3] = 0;
            }
        }
    }
    ctx.putImageData(img, 0, 0);
    return applyMasks({ canvas, width: w, height: h, stencil: false }, d, res, xref);
}

function placeholder(w: number, h: number): PdfImage {
    const canvas = makeCanvas(Math.max(1, Math.min(w, 64)), Math.max(1, Math.min(h, 64)));
    const c = canvas.getContext("2d") as CanvasRenderingContext2D;
    c.fillStyle = "#d0d0d0";
    c.fillRect(0, 0, canvas.width, canvas.height);
    return { canvas, width: canvas.width, height: canvas.height, stencil: false };
}

/** Applies /SMask (soft mask) and a stencil /Mask stream to the image's alpha. */
async function applyMasks(img: PdfImage, d: Dict, res: Dict | null, xref: XRef): Promise<PdfImage> {
    const sm = d.get("SMask");
    const mk = d.get("Mask");
    const mask = sm instanceof Stream ? sm : mk instanceof Stream ? mk : null;
    if (!mask) return img;
    const mi = await decodeImage(mask, res, xref);
    if (!mi) return img;
    const w = img.width, h = img.height;
    const out = makeCanvas(w, h);
    const oc = out.getContext("2d") as CanvasRenderingContext2D;
    oc.drawImage(img.canvas as CanvasImageSource, 0, 0);
    const base = oc.getImageData(0, 0, w, h);
    // scale the mask to the image size
    const mc = makeCanvas(w, h);
    const mctx = mc.getContext("2d") as CanvasRenderingContext2D;
    mctx.imageSmoothingEnabled = true;
    mctx.drawImage(mi.canvas as CanvasImageSource, 0, 0, w, h);
    const m = mctx.getImageData(0, 0, w, h).data;
    const bp = base.data;
    if (sm instanceof Stream) { for (let i = 0; i < w * h; i++) bp[i * 4 + 3] = m[i * 4]!; }
    else { for (let j = 0; j < w * h; j++) bp[j * 4 + 3] = m[j * 4 + 3]! ? 0 : 255; /* stencil Mask: painted pixels are masked out */ }
    oc.putImageData(base, 0, 0);
    return { canvas: out, width: w, height: h, stencil: false };
}

// ---- CCITT Group 3 / Group 4 fax ------------------------------------------------------------------------------------------------------

const WHITE_TERM: Array<[number, number, number]> = []; // [bits, code, run]
function buildTables() {
    if (WHITE_TERM.length) return;
    const wt = ["00110101", "000111", "0111", "1000", "1011", "1100", "1110", "1111", "10011", "10100", "00111", "01000", "001000", "000011", "110100", "110101", "101010", "101011", "0100111", "0001100", "0001000", "0010111", "0000011", "0000100", "0101000", "0101011", "0010011", "0100100", "0011000", "00000010", "00000011", "00011010", "00011011", "00010010", "00010011", "00010100", "00010101", "00010110", "00010111", "00101000", "00101001", "00101010", "00101011", "00101100", "00101101", "00000100", "00000101", "00001010", "00001011", "01010010", "01010011", "01010100", "01010101", "00100100", "00100101", "01011000", "01011001", "01011010", "01011011", "01001010", "01001011", "00110010", "00110011", "00110100"];
    const wm = ["11011", "10010", "010111", "0110111", "00110110", "00110111", "01100100", "01100101", "01101000", "01100111", "011001100", "011001101", "011010010", "011010011", "011010100", "011010101", "011010110", "011010111", "011011000", "011011001", "011011010", "011011011", "010011000", "010011001", "010011010", "011000", "010011011"];
    const bt = ["0000110111", "010", "11", "10", "011", "0011", "0010", "00011", "000101", "000100", "0000100", "0000101", "0000111", "00000100", "00000111", "000011000", "0000010111", "0000011000", "0000001000", "00001100111", "00001101000", "00001101100", "00000110111", "00000101000", "00000010111", "00000011000", "000011001010", "000011001011", "000011001100", "000011001101", "000001101000", "000001101001", "000001101010", "000001101011", "000011010010", "000011010011", "000011010100", "000011010101", "000011010110", "000011010111", "000001101100", "000001101101", "000011011010", "000011011011", "000001010100", "000001010101", "000001010110", "000001010111", "000001100100", "000001100101", "000001010010", "000001010011", "000000100100", "000000110111", "000000111000", "000000100111", "000000101000", "000001011000", "000001011001", "000000101011", "000000101100", "000001011010", "000001100110", "000001100111"];
    const bm = ["0000001111", "000011001000", "000011001001", "000001011011", "000000110011", "000000110100", "000000110101", "0000001101100", "0000001101101", "0000001001010", "0000001001011", "0000001001100", "0000001001101", "0000001110010", "0000001110011", "0000001110100", "0000001110101", "0000001110110", "0000001110111", "0000001010010", "0000001010011", "0000001010100", "0000001010101", "0000001011010", "0000001011011", "0000001100100", "0000001100101"];
    const ext = ["00000001000", "00000001100", "00000001101", "000000010010", "000000010011", "000000010100", "000000010101", "000000010110", "000000010111", "000000011100", "000000011101", "000000011110", "000000011111"];
    const add = (arr: string[], run0: number, step: number, isWhite: boolean) => { arr.forEach((c, i) => WHITE_TERM.push([isWhite ? 1 : 0, parseInt(c, 2) | (c.length << 16), run0 + i * step])); };
    // encode as [color, code | len<<16, run]
    WHITE_TERM.length = 0;
    add(wt, 0, 1, true);
    add(wm, 64, 64, true);
    add(bt, 0, 1, false);
    add(bm, 64, 64, false);
    add(ext, 1792, 64, true);
    add(ext, 1792, 64, false);
}

interface CcittOut { data: Uint8Array; rowBytes: number; blackIs1: boolean }

function ccitt(src: Uint8Array, w: number, h: number, parms: Dict | null): CcittOut | null {
    buildTables();
    const k = parms?.num("K", 0) ?? 0;
    const cols = parms?.num("Columns", 1728) || w;
    const blackIs1 = parms?.get("BlackIs1") === true;
    const byteAlign = parms?.get("EncodedByteAlign") === true;
    const rows = parms?.num("Rows", 0) || h;
    const rowBytes = (cols + 7) >> 3;
    const out = new Uint8Array(rowBytes * rows);
    // run-length lookup maps
    const maps: [Map<number, number>, Map<number, number>] = [new Map(), new Map()];
    for (const [col, cl, run] of WHITE_TERM) { const len = cl >> 16, code = cl & 0xffff; maps[col === 1 ? 0 : 1].set((len << 16) | code, run); }
    let bitPos = 0;
    const total = src.length * 8;
    const bit = () => { if (bitPos >= total) return -1; const b = (src[bitPos >> 3]! >> (7 - (bitPos & 7))) & 1; bitPos++; return b; };
    const peek = (n: number) => { let v = 0; for (let i = 0; i < n; i++) { const p = bitPos + i; v = (v << 1) | (p < total ? (src[p >> 3]! >> (7 - (p & 7))) & 1 : 0); } return v; };
    const readRun = (white: boolean): number => {
        let run = 0;
        for (;;) {
            let code = 0, len = 0, found = -1;
            while (len < 14) { const b = bit(); if (b < 0) return -1; code = (code << 1) | b; len++; const r = maps[white ? 0 : 1].get((len << 16) | code); if (r !== undefined) { found = r; break; } }
            if (found < 0) return -1;
            run += found;
            if (found < 64) return run;
        }
    };
    // reference and coding lines as arrays of changing elements
    let ref: number[] = [cols, cols];
    for (let y = 0; y < rows; y++) {
        if (byteAlign) bitPos = (bitPos + 7) & ~7;
        let cur: number[] = [];
        if (k < 0) {
            // Group 4
            let a0 = -1, color = 0; // 0 white
            let bi = 0;
            const bFind = () => {
                // b1: first changing element on ref line right of a0 with opposite colour to a0's colour
                while (bi > 0 && ref[bi - 1]! > a0) bi--;
                while (bi < ref.length && (ref[bi]! <= a0 || (bi & 1) !== color)) bi++;
                return [bi < ref.length ? ref[bi]! : cols, bi + 1 < ref.length ? ref[bi + 1]! : cols];
            };
            while (a0 < cols) {
                const [b1 = cols, b2 = cols] = bFind();
                if (peek(1) === 1) { bitPos += 1; a0 = b1; cur.push(a0); color ^= 1; }
                else if (peek(3) === 0b011) { bitPos += 3; a0 = b1 + 1; cur.push(a0); color ^= 1; }
                else if (peek(3) === 0b010) { bitPos += 3; a0 = b1 - 1; cur.push(a0); color ^= 1; }
                else if (peek(3) === 0b001) { bitPos += 3; const r1 = readRun(color === 0), r2 = readRun(color !== 0); if (r1 < 0 || r2 < 0) break; const s = Math.max(a0, 0); cur.push(s + r1, s + r1 + r2); a0 = s + r1 + r2; }
                else if (peek(4) === 0b0001) { bitPos += 4; a0 = b2; }
                else if (peek(6) === 0b000011) { bitPos += 6; a0 = b1 + 2; cur.push(a0); color ^= 1; }
                else if (peek(6) === 0b000010) { bitPos += 6; a0 = b1 - 2; cur.push(a0); color ^= 1; }
                else if (peek(7) === 0b0000011) { bitPos += 7; a0 = b1 + 3; cur.push(a0); color ^= 1; }
                else if (peek(7) === 0b0000010) { bitPos += 7; a0 = b1 - 3; cur.push(a0); color ^= 1; }
                else { return y > 0 ? finish() : null; }
            }
        } else {
            // Group 3 (1-D): runs alternate white, black; skip an EOL if present
            while (peek(12) === 1 || peek(11) === 0) { if (peek(12) === 1) { bitPos += 12; break; } bitPos++; if (bitPos >= total) break; }
            let x = 0, white = true;
            while (x < cols) { const r = readRun(white); if (r < 0) break; x += r; cur.push(Math.min(x, cols)); white = !white; }
        }
        cur = cur.filter((v, i, a) => i === 0 || v >= a[i - 1]!);
        // paint: cur lists changing elements; colours alternate starting with white
        let x = 0, black = false;
        const row = y * rowBytes;
        for (const c of [...cur, cols]) {
            const to = Math.min(Math.max(c, 0), cols);
            if (black) for (let i = x; i < to; i++) out[row + (i >> 3)]! |= 0x80 >> (i & 7);
            x = Math.max(x, to);
            black = !black;
        }
        ref = [...cur, cols, cols];
    }
    return finish();
    function finish(): CcittOut {
        // the file's pixels are 1 = black above; the PDF default (BlackIs1 false) wants 0 = black
        const o = new Uint8Array(out.length);
        for (let i = 0; i < out.length; i++) o[i] = blackIs1 ? out[i]! : ~out[i]! & 255;
        return { data: o, rowBytes, blackIs1 };
    }
}

export { DEVICE_CMYK, DEVICE_RGB, PName };
